// Curated library of common application log locations + monitoring patterns.
//
// Accuracy contract:
//  - Every `regex` is a valid JavaScript RegExp source string.
//  - Every `regex` matches its own `exampleLine` (verified by the
//    self-check in ToolClient — see verifyPatterns()).
//  - Paths reflect stock package defaults; Debian/Ubuntu and RHEL/CentOS
//    paths are listed separately where they differ.
//
// The Alert24 agent config blocks emitted from this data follow the
// `log_searches` "file" source schema documented in
// alert24-app2/docs/agent-log-searches.md:
//   { "name", "source": "file", "path", "pattern", "pattern_type": "regex" }

export type Severity = 'info' | 'warning' | 'high' | 'critical';

export interface LogPattern {
  /** Suggested Alert24 search name (snake_case, used as the config `name`). */
  id: string;
  /** Human-readable pattern name. */
  name: string;
  /** What this pattern catches / why you would monitor it. */
  catches: string;
  severity: Severity;
  /** Valid JS regex source. Must match exampleLine. */
  regex: string;
  /** A real-world log line this regex matches. */
  exampleLine: string;
  /**
   * Path override for the generated Alert24 config block. Defaults to the
   * application's primary path (first entry of the relevant log group).
   */
  path?: string;
}

export interface LogPath {
  /** What this file/glob contains. */
  label: string;
  /** Debian/Ubuntu default path or glob. */
  debian: string;
  /** RHEL/CentOS/Fedora default path or glob (omit if identical). */
  rhel?: string;
}

export interface AppLog {
  /** URL anchor + filter key. */
  slug: string;
  name: string;
  /** Short tagline shown under the heading. */
  tagline: string;
  category: 'Web servers' | 'Databases' | 'Caching & queues' | 'System & auth' | 'Containers' | 'Runtimes' | 'Mail & security' | 'Scheduling';
  /** Default log file locations. */
  paths: LogPath[];
  /** Representative raw log lines (format reference). */
  formatExamples: string[];
  patterns: LogPattern[];
}

export const APPS: AppLog[] = [
  // ───────────────────────────── nginx ─────────────────────────────
  {
    slug: 'nginx',
    name: 'nginx',
    tagline: 'Access & error logs for the nginx web server / reverse proxy.',
    category: 'Web servers',
    paths: [
      { label: 'Access log', debian: '/var/log/nginx/access.log' },
      { label: 'Error log', debian: '/var/log/nginx/error.log' },
    ],
    formatExamples: [
      '192.0.2.10 - - [01/Jun/2026:10:15:42 +0000] "GET /api/orders HTTP/1.1" 502 166 "-" "curl/8.4.0"',
      '2026/06/01 10:15:42 [error] 2913#2913: *5 upstream timed out (110: Connection timed out) while reading response header from upstream, client: 192.0.2.10, server: example.com, request: "GET /api/orders HTTP/1.1", upstream: "http://127.0.0.1:8080/api/orders", host: "example.com"',
    ],
    patterns: [
      {
        id: 'nginx_5xx',
        name: '5xx server errors (access log)',
        catches: 'Any 5xx response status — backend failures, gateway errors, overload.',
        severity: 'high',
        regex: '" 5\\d{2} ',
        exampleLine: '192.0.2.10 - - [01/Jun/2026:10:15:42 +0000] "GET /api/orders HTTP/1.1" 502 166 "-" "curl/8.4.0"',
        path: '/var/log/nginx/access.log',
      },
      {
        id: 'nginx_4xx',
        name: '4xx client errors (access log)',
        catches: '4xx responses — broken links, auth failures, probing/scanning.',
        severity: 'warning',
        regex: '" 4\\d{2} ',
        exampleLine: '198.51.100.7 - - [01/Jun/2026:10:16:01 +0000] "GET /wp-login.php HTTP/1.1" 404 153 "-" "Mozilla/5.0"',
        path: '/var/log/nginx/access.log',
      },
      {
        id: 'nginx_upstream_timeout',
        name: 'Upstream timed out',
        catches: 'Backend/upstream did not respond in time — slow or dead app server.',
        severity: 'high',
        regex: 'upstream timed out',
        exampleLine: '2026/06/01 10:15:42 [error] 2913#2913: *5 upstream timed out (110: Connection timed out) while reading response header from upstream, client: 192.0.2.10, server: example.com',
        path: '/var/log/nginx/error.log',
      },
      {
        id: 'nginx_conn_refused',
        name: 'Connection refused by upstream',
        catches: 'Upstream is down — connect() failed / connection refused.',
        severity: 'critical',
        regex: 'connect\\(\\) failed \\(111: Connection refused\\)',
        exampleLine: '2026/06/01 10:17:03 [error] 2913#2913: *9 connect() failed (111: Connection refused) while connecting to upstream, client: 192.0.2.10, server: example.com, upstream: "http://127.0.0.1:8080/"',
        path: '/var/log/nginx/error.log',
      },
      {
        id: 'nginx_worker_limit',
        name: 'Worker connections exhausted',
        catches: 'nginx is out of worker connections — raise worker_connections.',
        severity: 'high',
        regex: 'worker_connections are not enough',
        exampleLine: '2026/06/01 10:18:20 [alert] 2913#2913: 1024 worker_connections are not enough while connecting to upstream',
        path: '/var/log/nginx/error.log',
      },
    ],
  },

  // ───────────────────────── Apache httpd ──────────────────────────
  {
    slug: 'apache',
    name: 'Apache httpd',
    tagline: 'Access & error logs for the Apache HTTP Server.',
    category: 'Web servers',
    paths: [
      { label: 'Access log', debian: '/var/log/apache2/access.log', rhel: '/var/log/httpd/access_log' },
      { label: 'Error log', debian: '/var/log/apache2/error.log', rhel: '/var/log/httpd/error_log' },
    ],
    formatExamples: [
      '192.0.2.10 - - [01/Jun/2026:10:15:42 +0000] "GET /index.html HTTP/1.1" 500 4523 "-" "Mozilla/5.0"',
      '[Mon Jun 01 10:15:42.123456 2026] [proxy:error] [pid 2913:tid 140] (111)Connection refused: AH00957: HTTP: attempt to connect to 127.0.0.1:8080 (localhost) failed',
    ],
    patterns: [
      {
        id: 'apache_5xx',
        name: '5xx server errors (access log)',
        catches: 'Any 5xx response — application or proxy failures.',
        severity: 'high',
        regex: '" 5\\d{2} ',
        exampleLine: '192.0.2.10 - - [01/Jun/2026:10:15:42 +0000] "GET /index.html HTTP/1.1" 500 4523 "-" "Mozilla/5.0"',
        path: '/var/log/apache2/access.log',
      },
      {
        id: 'apache_segfault',
        name: 'Child segfault / exit signal',
        catches: 'A worker process crashed (segmentation fault) — often a buggy module.',
        severity: 'critical',
        regex: 'exit signal Segmentation fault',
        exampleLine: '[Mon Jun 01 10:20:11.000000 2026] [core:notice] [pid 1:tid 0] AH00052: child pid 4012 exit signal Segmentation fault (11)',
        path: '/var/log/apache2/error.log',
      },
      {
        id: 'apache_maxclients',
        name: 'MaxRequestWorkers reached',
        catches: 'Apache hit its worker ceiling — requests are queuing, raise the limit.',
        severity: 'high',
        regex: 'reached MaxRequestWorkers setting',
        exampleLine: '[Mon Jun 01 10:21:00.000000 2026] [mpm_event:error] [pid 2913:tid 140] AH00484: server reached MaxRequestWorkers setting, consider raising the MaxRequestWorkers setting',
        path: '/var/log/apache2/error.log',
      },
      {
        id: 'apache_proxy_refused',
        name: 'Proxy connection refused',
        catches: 'Backend behind mod_proxy is unreachable.',
        severity: 'critical',
        regex: 'AH00957: \\w+: attempt to connect to .* failed',
        exampleLine: '[Mon Jun 01 10:15:42.123456 2026] [proxy:error] [pid 2913:tid 140] (111)Connection refused: AH00957: HTTP: attempt to connect to 127.0.0.1:8080 (localhost) failed',
        path: '/var/log/apache2/error.log',
      },
    ],
  },

  // ──────────────────────── MySQL / MariaDB ────────────────────────
  {
    slug: 'mysql',
    name: 'MySQL / MariaDB',
    tagline: 'Error log and slow query log for MySQL and MariaDB servers.',
    category: 'Databases',
    paths: [
      { label: 'Error log', debian: '/var/log/mysql/error.log', rhel: '/var/log/mysqld.log' },
      { label: 'Slow query log', debian: '/var/log/mysql/mysql-slow.log', rhel: '/var/log/mysql-slow.log' },
    ],
    formatExamples: [
      "2026-06-01T10:15:42.123456Z 8 [ERROR] [MY-010914] [Server] Got an error reading communication packets",
      "2026-06-01T10:15:42.123456Z 0 [Warning] [MY-010055] Too many connections",
      "# Query_time: 12.840000  Lock_time: 0.000100 Rows_sent: 1  Rows_examined: 4821990",
    ],
    patterns: [
      {
        id: 'mysql_too_many_conns',
        name: 'Too many connections',
        catches: 'Connection limit reached — clients are being rejected. Raise max_connections or fix a leak.',
        severity: 'critical',
        regex: 'Too many connections',
        exampleLine: '2026-06-01T10:15:42.123456Z 0 [Warning] [MY-010055] Too many connections',
        path: '/var/log/mysql/error.log',
      },
      {
        id: 'mysql_deadlock',
        name: 'Deadlock detected',
        catches: 'InnoDB rolled back a transaction to break a deadlock.',
        severity: 'high',
        regex: 'Deadlock found when trying to get lock',
        exampleLine: '2026-06-01T10:16:10.000000Z 12 [Warning] [MY-013360] InnoDB: Deadlock found when trying to get lock; try restarting transaction',
        path: '/var/log/mysql/error.log',
      },
      {
        id: 'mysql_aborted_connection',
        name: 'Aborted connection',
        catches: 'Clients disconnecting mid-query — network issues, timeouts, or crashing app workers.',
        severity: 'warning',
        regex: 'Aborted connection \\d+ to db',
        exampleLine: "2026-06-01T10:17:01.000000Z 33 [Note] [MY-010914] [Server] Aborted connection 33 to db: 'app' user: 'app' host: '10.0.0.5' (Got timeout reading communication packets)",
        path: '/var/log/mysql/error.log',
      },
      {
        id: 'mysql_crash_recovery',
        name: 'Crash recovery / InnoDB error',
        catches: 'InnoDB encountered an error or is recovering after an unclean shutdown.',
        severity: 'critical',
        regex: '\\[ERROR\\].*InnoDB',
        exampleLine: '2026-06-01T10:18:00.000000Z 0 [ERROR] [MY-012560] InnoDB: The log sequence number is in the future!',
        path: '/var/log/mysql/error.log',
      },
      {
        id: 'mysql_slow_query',
        name: 'Slow query over 10s',
        catches: 'Slow-log entries whose Query_time exceeds 10 seconds.',
        severity: 'warning',
        regex: '# Query_time: (?:[1-9]\\d|\\d{2,})\\.',
        exampleLine: '# Query_time: 12.840000  Lock_time: 0.000100 Rows_sent: 1  Rows_examined: 4821990',
        path: '/var/log/mysql/mysql-slow.log',
      },
    ],
  },

  // ───────────────────────── PostgreSQL ────────────────────────────
  {
    slug: 'postgres',
    name: 'PostgreSQL',
    tagline: 'Server log for PostgreSQL (stderr / logging collector).',
    category: 'Databases',
    paths: [
      { label: 'Log directory', debian: '/var/log/postgresql/postgresql-*.log', rhel: '/var/lib/pgsql/data/log/postgresql-*.log' },
    ],
    formatExamples: [
      '2026-06-01 10:15:42.123 UTC [2913] FATAL:  password authentication failed for user "app"',
      '2026-06-01 10:16:10.000 UTC [3001] ERROR:  deadlock detected',
      '2026-06-01 10:17:00.000 UTC [3050] LOG:  duration: 8421.337 ms  statement: SELECT * FROM orders',
    ],
    patterns: [
      {
        id: 'postgres_fatal',
        name: 'FATAL messages',
        catches: 'Connection/auth failures, too many clients, recovery aborts — anything PostgreSQL logs as FATAL.',
        severity: 'high',
        regex: '\\bFATAL:',
        exampleLine: '2026-06-01 10:15:42.123 UTC [2913] FATAL:  password authentication failed for user "app"',
        path: '/var/log/postgresql/postgresql-*.log',
      },
      {
        id: 'postgres_deadlock',
        name: 'Deadlock detected',
        catches: 'A transaction was aborted to resolve a deadlock.',
        severity: 'high',
        regex: 'deadlock detected',
        exampleLine: '2026-06-01 10:16:10.000 UTC [3001] ERROR:  deadlock detected',
        path: '/var/log/postgresql/postgresql-*.log',
      },
      {
        id: 'postgres_too_many_clients',
        name: 'Too many clients',
        catches: 'Connection slots exhausted — raise max_connections or add a pooler (PgBouncer).',
        severity: 'critical',
        regex: 'too many clients already',
        exampleLine: '2026-06-01 10:16:30.000 UTC [3010] FATAL:  sorry, too many clients already',
        path: '/var/log/postgresql/postgresql-*.log',
      },
      {
        id: 'postgres_slow_statement',
        name: 'Slow statement (duration)',
        catches: 'Statements logged by log_min_duration_statement taking over 1 second.',
        severity: 'warning',
        regex: 'duration: \\d{4,}\\.\\d+ ms',
        exampleLine: '2026-06-01 10:17:00.000 UTC [3050] LOG:  duration: 8421.337 ms  statement: SELECT * FROM orders',
        path: '/var/log/postgresql/postgresql-*.log',
      },
      {
        id: 'postgres_checkpoint_frequent',
        name: 'Checkpoints occurring too frequently',
        catches: 'WAL checkpoints firing too often — increase max_wal_size to reduce I/O.',
        severity: 'warning',
        regex: 'checkpoints are occurring too frequently',
        exampleLine: '2026-06-01 10:18:00.000 UTC [2900] LOG:  checkpoints are occurring too frequently (9 seconds apart)',
        path: '/var/log/postgresql/postgresql-*.log',
      },
    ],
  },

  // ─────────────────────────── Redis ───────────────────────────────
  {
    slug: 'redis',
    name: 'Redis',
    tagline: 'Server log for the Redis in-memory data store.',
    category: 'Caching & queues',
    paths: [
      { label: 'Server log', debian: '/var/log/redis/redis-server.log', rhel: '/var/log/redis/redis.log' },
    ],
    formatExamples: [
      '2913:M 01 Jun 2026 10:15:42.123 # MISCONF Redis is configured to save RDB snapshots, but it is currently not able to persist on disk.',
      '2913:M 01 Jun 2026 10:16:10.000 * Background saving terminated with success',
    ],
    patterns: [
      {
        id: 'redis_misconf',
        name: 'MISCONF persistence error',
        catches: 'Redis cannot persist to disk and is rejecting writes — full disk or broken RDB/AOF.',
        severity: 'critical',
        regex: 'MISCONF',
        exampleLine: '2913:M 01 Jun 2026 10:15:42.123 # MISCONF Redis is configured to save RDB snapshots, but it is currently not able to persist on disk.',
        path: '/var/log/redis/redis-server.log',
      },
      {
        id: 'redis_bgsave_failed',
        name: 'Background save failed',
        catches: 'A background RDB save (fork) failed — out of memory or disk.',
        severity: 'high',
        regex: 'Background saving error',
        exampleLine: '2913:M 01 Jun 2026 10:17:00.000 # Background saving error',
        path: '/var/log/redis/redis-server.log',
      },
      {
        id: 'redis_oom',
        name: 'OOM command rejected',
        catches: 'Writes rejected because maxmemory was hit with no eviction policy.',
        severity: 'critical',
        regex: 'OOM command not allowed',
        exampleLine: "2913:M 01 Jun 2026 10:18:00.000 # OOM command not allowed when used memory > 'maxmemory'.",
        path: '/var/log/redis/redis-server.log',
      },
      {
        id: 'redis_overcommit',
        name: 'Memory overcommit warning',
        catches: 'vm.overcommit_memory not set to 1 — background saves and replication may fail under load.',
        severity: 'warning',
        regex: 'overcommit_memory is set to 0',
        exampleLine: '2913:M 01 Jun 2026 10:14:00.000 # WARNING overcommit_memory is set to 0! Background save may fail under low memory condition.',
        path: '/var/log/redis/redis-server.log',
      },
    ],
  },

  // ─────────────────────────── MongoDB ─────────────────────────────
  {
    slug: 'mongodb',
    name: 'MongoDB',
    tagline: 'Structured JSON log (mongod) for MongoDB 4.4+.',
    category: 'Databases',
    paths: [
      { label: 'Server log', debian: '/var/log/mongodb/mongod.log' },
    ],
    formatExamples: [
      '{"t":{"$date":"2026-06-01T10:15:42.123+00:00"},"s":"E","c":"NETWORK","id":22942,"ctx":"conn5","msg":"Error receiving request from client. Ending connection from remote","attr":{"error":{"code":141}}}',
      '{"t":{"$date":"2026-06-01T10:17:00.000+00:00"},"s":"I","c":"COMMAND","ctx":"conn9","msg":"Slow query","attr":{"durationMillis":4210}}',
    ],
    patterns: [
      {
        id: 'mongodb_severe',
        name: 'Error / Fatal severity',
        catches: 'Log entries with severity "E" (error) or "F" (fatal).',
        severity: 'high',
        regex: '"s":"[EF]"',
        exampleLine: '{"t":{"$date":"2026-06-01T10:15:42.123+00:00"},"s":"E","c":"NETWORK","id":22942,"ctx":"conn5","msg":"Error receiving request from client. Ending connection from remote"}',
        path: '/var/log/mongodb/mongod.log',
      },
      {
        id: 'mongodb_slow_query',
        name: 'Slow query',
        catches: 'Operations exceeding the slow-op threshold (default 100ms).',
        severity: 'warning',
        regex: '"msg":"Slow query"',
        exampleLine: '{"t":{"$date":"2026-06-01T10:17:00.000+00:00"},"s":"I","c":"COMMAND","ctx":"conn9","msg":"Slow query","attr":{"durationMillis":4210}}',
        path: '/var/log/mongodb/mongod.log',
      },
      {
        id: 'mongodb_election',
        name: 'Replica set election',
        catches: 'A new primary is being elected — replica set instability or failover.',
        severity: 'high',
        regex: '"msg":"Starting an election',
        exampleLine: '{"t":{"$date":"2026-06-01T10:18:00.000+00:00"},"s":"I","c":"ELECTION","ctx":"conn1","msg":"Starting an election, since we have not seen a primary"}',
        path: '/var/log/mongodb/mongod.log',
      },
      {
        id: 'mongodb_wt_error',
        name: 'WiredTiger storage error',
        catches: 'Storage-engine errors — disk problems or corruption.',
        severity: 'critical',
        regex: '"c":"STORAGE".*"s":"E"',
        exampleLine: '{"t":{"$date":"2026-06-01T10:19:00.000+00:00"},"c":"STORAGE","s":"E","ctx":"conn1","msg":"WiredTiger error","attr":{"error":-31802}}',
        path: '/var/log/mongodb/mongod.log',
      },
    ],
  },

  // ────────────────────── sshd / auth.log ──────────────────────────
  {
    slug: 'sshd',
    name: 'sshd / auth.log',
    tagline: 'SSH daemon authentication events (PAM / auth facility).',
    category: 'System & auth',
    paths: [
      { label: 'Auth log', debian: '/var/log/auth.log', rhel: '/var/log/secure' },
    ],
    formatExamples: [
      'Jun  1 10:15:42 web-01 sshd[2913]: Failed password for invalid user admin from 198.51.100.7 port 51022 ssh2',
      'Jun  1 10:16:10 web-01 sshd[2914]: Accepted publickey for deploy from 10.0.0.5 port 51044 ssh2: RSA SHA256:abc123',
    ],
    patterns: [
      {
        id: 'sshd_failed_password',
        name: 'Failed password',
        catches: 'Failed SSH password attempts — brute-force probing if frequent.',
        severity: 'high',
        regex: 'Failed password for',
        exampleLine: 'Jun  1 10:15:42 web-01 sshd[2913]: Failed password for invalid user admin from 198.51.100.7 port 51022 ssh2',
        path: '/var/log/auth.log',
      },
      {
        id: 'sshd_invalid_user',
        name: 'Invalid user',
        catches: 'Login attempts for users that do not exist — automated scanning.',
        severity: 'warning',
        regex: 'Invalid user \\S+ from',
        exampleLine: 'Jun  1 10:15:41 web-01 sshd[2913]: Invalid user admin from 198.51.100.7 port 51022',
        path: '/var/log/auth.log',
      },
      {
        id: 'sshd_accepted',
        name: 'Accepted login',
        catches: 'Successful SSH logins (publickey or password) — track who got in.',
        severity: 'info',
        regex: 'Accepted (?:publickey|password) for',
        exampleLine: 'Jun  1 10:16:10 web-01 sshd[2914]: Accepted publickey for deploy from 10.0.0.5 port 51044 ssh2: RSA SHA256:abc123',
        path: '/var/log/auth.log',
      },
      {
        id: 'sshd_root_login',
        name: 'Root login attempt',
        catches: 'Any authentication targeting the root account directly.',
        severity: 'high',
        regex: '(?:Failed|Accepted) \\S+ for root from',
        exampleLine: 'Jun  1 10:17:00 web-01 sshd[2915]: Failed password for root from 198.51.100.7 port 51066 ssh2',
        path: '/var/log/auth.log',
      },
    ],
  },

  // ─────────────────── syslog / OOM / systemd ──────────────────────
  {
    slug: 'syslog',
    name: 'syslog / kernel (OOM)',
    tagline: 'System log — kernel messages, OOM killer, and service crashes.',
    category: 'System & auth',
    paths: [
      { label: 'System log', debian: '/var/log/syslog', rhel: '/var/log/messages' },
      { label: 'Kernel log', debian: '/var/log/kern.log', rhel: '/var/log/messages' },
    ],
    formatExamples: [
      'Jun  1 10:15:42 web-01 kernel: [12345.678901] Out of memory: Killed process 4012 (mysqld) total-vm:8400000kB',
      'Jun  1 10:16:10 web-01 systemd[1]: nginx.service: Main process exited, code=killed, status=9/KILL',
    ],
    patterns: [
      {
        id: 'oom_killer',
        name: 'OOM killer invoked',
        catches: 'The kernel killed a process to reclaim memory — server is out of RAM.',
        severity: 'critical',
        regex: 'Out of memory: Killed process',
        exampleLine: 'Jun  1 10:15:42 web-01 kernel: [12345.678901] Out of memory: Killed process 4012 (mysqld) total-vm:8400000kB',
        path: '/var/log/syslog',
      },
      {
        id: 'oom_invoked',
        name: 'oom-killer triggered',
        catches: 'A process invoked the OOM killer — memory pressure event.',
        severity: 'high',
        regex: 'invoked oom-killer',
        exampleLine: 'Jun  1 10:15:41 web-01 kernel: [12345.600000] nginx invoked oom-killer: gfp_mask=0x100cca, order=0, oom_score_adj=0',
        path: '/var/log/syslog',
      },
      {
        id: 'kernel_io_error',
        name: 'Disk / I/O error',
        catches: 'Block-layer I/O errors — failing or saturated storage.',
        severity: 'critical',
        regex: 'I/O error.*dev \\w+',
        exampleLine: 'Jun  1 10:18:00 web-01 kernel: [12400.000000] blk_update_request: I/O error, dev sda, sector 123456789',
        path: '/var/log/kern.log',
      },
      {
        id: 'systemd_killed',
        name: 'Service killed by signal',
        catches: 'A systemd-managed service was killed (e.g. SIGKILL) — crash or OOM.',
        severity: 'high',
        regex: 'Main process exited, code=killed',
        exampleLine: 'Jun  1 10:16:10 web-01 systemd[1]: nginx.service: Main process exited, code=killed, status=9/KILL',
        path: '/var/log/syslog',
      },
    ],
  },

  // ──────────────────────── systemd journal ────────────────────────
  {
    slug: 'systemd',
    name: 'systemd journal',
    tagline: 'The systemd journal (journalctl). On Alert24 prefer the journald source for live units.',
    category: 'System & auth',
    paths: [
      { label: 'Journal store (binary)', debian: '/var/log/journal/ (query with journalctl)' },
      { label: 'Persisted text (if ForwardToSyslog)', debian: '/var/log/syslog', rhel: '/var/log/messages' },
    ],
    formatExamples: [
      'Jun 01 10:15:42 web-01 myapp[2913]: ERROR could not connect to database: connection refused',
      'Jun 01 10:16:10 web-01 systemd[1]: myapp.service: Failed with result \'exit-code\'.',
    ],
    patterns: [
      {
        id: 'systemd_unit_failed',
        name: 'Unit failed',
        catches: 'A service entered the failed state.',
        severity: 'high',
        regex: 'Failed with result',
        exampleLine: "Jun 01 10:16:10 web-01 systemd[1]: myapp.service: Failed with result 'exit-code'.",
        path: '/var/log/syslog',
      },
      {
        id: 'systemd_start_limit',
        name: 'Start request repeated too quickly',
        catches: 'A service is crash-looping and systemd stopped restarting it.',
        severity: 'critical',
        regex: 'Start request repeated too quickly',
        exampleLine: 'Jun 01 10:17:00 web-01 systemd[1]: myapp.service: Start request repeated too quickly.',
        path: '/var/log/syslog',
      },
      {
        id: 'systemd_app_error',
        name: 'Application ERROR/FATAL',
        catches: 'Generic ERROR or FATAL emitted by a service to the journal.',
        severity: 'high',
        regex: '\\b(?:ERROR|FATAL)\\b',
        exampleLine: 'Jun 01 10:15:42 web-01 myapp[2913]: ERROR could not connect to database: connection refused',
        path: '/var/log/syslog',
      },
    ],
  },

  // ────────────────────────── PHP-FPM ──────────────────────────────
  {
    slug: 'php-fpm',
    name: 'PHP-FPM',
    tagline: 'FastCGI Process Manager pool/error logs for PHP.',
    category: 'Runtimes',
    paths: [
      { label: 'FPM error log', debian: '/var/log/php8.2-fpm.log', rhel: '/var/log/php-fpm/error.log' },
      { label: 'Pool log (www)', debian: '/var/log/php-fpm/www-error.log', rhel: '/var/log/php-fpm/www-error.log' },
    ],
    formatExamples: [
      '[01-Jun-2026 10:15:42] WARNING: [pool www] server reached pm.max_children setting (50), consider raising it',
      '[01-Jun-2026 10:16:10] WARNING: [pool www] child 4012 exited on signal 11 (SIGSEGV) after 120.5 seconds from start',
    ],
    patterns: [
      {
        id: 'phpfpm_max_children',
        name: 'max_children reached',
        catches: 'The FPM pool is saturated — requests are queuing. Raise pm.max_children.',
        severity: 'high',
        regex: 'reached pm\\.max_children setting',
        exampleLine: '[01-Jun-2026 10:15:42] WARNING: [pool www] server reached pm.max_children setting (50), consider raising it',
        path: '/var/log/php8.2-fpm.log',
      },
      {
        id: 'phpfpm_child_segfault',
        name: 'Worker segfault',
        catches: 'An FPM worker crashed (SIGSEGV) — buggy extension or code.',
        severity: 'critical',
        regex: 'exited on signal 11 \\(SIGSEGV\\)',
        exampleLine: '[01-Jun-2026 10:16:10] WARNING: [pool www] child 4012 exited on signal 11 (SIGSEGV) after 120.5 seconds from start',
        path: '/var/log/php8.2-fpm.log',
      },
      {
        id: 'phpfpm_exec_timeout',
        name: 'Script execution timeout',
        catches: 'A request exceeded request_terminate_timeout and was killed.',
        severity: 'high',
        regex: 'execution timed out',
        exampleLine: '[01-Jun-2026 10:17:00] WARNING: [pool www] child 4015, script \'/var/www/app/index.php\' (request: "GET /index.php") execution timed out (35.123456 sec), terminating',
        path: '/var/log/php8.2-fpm.log',
      },
    ],
  },

  // ───────────────────── Node.js / PM2 ─────────────────────────────
  {
    slug: 'nodejs',
    name: 'Node.js / PM2',
    tagline: 'Application stdout/stderr captured by PM2 (or systemd).',
    category: 'Runtimes',
    paths: [
      { label: 'PM2 error log', debian: '~/.pm2/logs/<app>-error.log' },
      { label: 'PM2 output log', debian: '~/.pm2/logs/<app>-out.log' },
      { label: 'PM2 daemon log', debian: '~/.pm2/pm2.log' },
    ],
    formatExamples: [
      'Error: connect ECONNREFUSED 127.0.0.1:5432',
      'FATAL ERROR: Reached heap limit Allocation failed - JavaScript heap out of memory',
    ],
    patterns: [
      {
        id: 'node_unhandled_rejection',
        name: 'Unhandled promise rejection',
        catches: 'A rejected promise with no handler — a latent crash in newer Node versions.',
        severity: 'high',
        regex: 'UnhandledPromiseRejection',
        exampleLine: '[2026-06-01T10:15:42.123Z] UnhandledPromiseRejectionWarning: Error: connect ECONNREFUSED 127.0.0.1:5432',
        path: '~/.pm2/logs/app-error.log',
      },
      {
        id: 'node_heap_oom',
        name: 'JavaScript heap out of memory',
        catches: 'V8 ran out of heap and the process aborted — raise --max-old-space-size or fix a leak.',
        severity: 'critical',
        regex: 'JavaScript heap out of memory',
        exampleLine: 'FATAL ERROR: Reached heap limit Allocation failed - JavaScript heap out of memory',
        path: '~/.pm2/logs/app-error.log',
      },
      {
        id: 'node_econnrefused',
        name: 'ECONNREFUSED',
        catches: 'A downstream service (DB, cache, API) refused the connection.',
        severity: 'high',
        regex: 'ECONNREFUSED',
        exampleLine: 'Error: connect ECONNREFUSED 127.0.0.1:5432',
        path: '~/.pm2/logs/app-error.log',
      },
      {
        id: 'node_uncaught',
        name: 'Uncaught exception',
        catches: 'An exception bubbled to the top of the event loop — process likely exited.',
        severity: 'critical',
        regex: 'uncaughtException',
        exampleLine: '[2026-06-01T10:17:00.000Z] uncaughtException: TypeError: Cannot read properties of undefined',
        path: '~/.pm2/logs/app-error.log',
      },
    ],
  },

  // ──────────────────────── HAProxy ────────────────────────────────
  {
    slug: 'haproxy',
    name: 'HAProxy',
    tagline: 'Load-balancer logs (via syslog/journald), default HTTP log format.',
    category: 'Web servers',
    paths: [
      { label: 'HAProxy log', debian: '/var/log/haproxy.log', rhel: '/var/log/haproxy.log' },
    ],
    formatExamples: [
      'Jun  1 10:15:42 lb-01 haproxy[2913]: 192.0.2.10:51022 [01/Jun/2026:10:15:42.123] fe_http be_app/web1 0/0/1/12/13 503 217 - - SC-- 5/5/0/0/0 0/0 "GET /api HTTP/1.1"',
      'Jun  1 10:16:10 lb-01 haproxy[2913]: Server be_app/web2 is DOWN, reason: Layer4 connection problem, info: "Connection refused"',
    ],
    patterns: [
      {
        id: 'haproxy_5xx',
        name: '5xx returned to client',
        catches: 'HAProxy returned a 5xx — backend errors or HAProxy-generated 503 (no server available).',
        severity: 'high',
        regex: ' (?:500|502|503|504) \\d+ ',
        exampleLine: 'Jun  1 10:15:42 lb-01 haproxy[2913]: 192.0.2.10:51022 [01/Jun/2026:10:15:42.123] fe_http be_app/web1 0/0/1/12/13 503 217 - - SC-- 5/5/0/0/0 0/0 "GET /api HTTP/1.1"',
        path: '/var/log/haproxy.log',
      },
      {
        id: 'haproxy_server_down',
        name: 'Backend server DOWN',
        catches: 'A health check marked a backend server down.',
        severity: 'critical',
        regex: 'Server \\S+ is DOWN',
        exampleLine: 'Jun  1 10:16:10 lb-01 haproxy[2913]: Server be_app/web2 is DOWN, reason: Layer4 connection problem, info: "Connection refused"',
        path: '/var/log/haproxy.log',
      },
      {
        id: 'haproxy_no_server',
        name: 'No server available',
        catches: 'A backend has no healthy servers left — full outage for that pool.',
        severity: 'critical',
        regex: 'backend \\S+ has no server available',
        exampleLine: 'Jun  1 10:17:00 lb-01 haproxy[2913]: backend be_app has no server available!',
        path: '/var/log/haproxy.log',
      },
    ],
  },

  // ───────────────────────── Postfix ───────────────────────────────
  {
    slug: 'postfix',
    name: 'Postfix',
    tagline: 'Mail Transfer Agent log (maillog).',
    category: 'Mail & security',
    paths: [
      { label: 'Mail log', debian: '/var/log/mail.log', rhel: '/var/log/maillog' },
      { label: 'Mail errors', debian: '/var/log/mail.err', rhel: '/var/log/maillog' },
    ],
    formatExamples: [
      'Jun  1 10:15:42 mail-01 postfix/smtp[2913]: A1B2C3: to=<user@example.org>, relay=mx.example.org[203.0.113.5]:25, delay=2.1, status=bounced (host mx.example.org said: 550 5.1.1 User unknown)',
      'Jun  1 10:16:10 mail-01 postfix/smtpd[2914]: warning: hostname unknown: address not listed for hostname',
    ],
    patterns: [
      {
        id: 'postfix_bounced',
        name: 'Message bounced',
        catches: 'A message was bounced by the remote MTA — bad recipient or policy reject.',
        severity: 'warning',
        regex: 'status=bounced',
        exampleLine: 'Jun  1 10:15:42 mail-01 postfix/smtp[2913]: A1B2C3: to=<user@example.org>, relay=mx.example.org[203.0.113.5]:25, status=bounced (host mx.example.org said: 550 5.1.1 User unknown)',
        path: '/var/log/mail.log',
      },
      {
        id: 'postfix_deferred',
        name: 'Message deferred',
        catches: 'Delivery was deferred and will be retried — connectivity or greylisting.',
        severity: 'warning',
        regex: 'status=deferred',
        exampleLine: 'Jun  1 10:16:00 mail-01 postfix/smtp[2913]: D4E5F6: to=<user@example.net>, relay=none, status=deferred (connect to example.net: Connection timed out)',
        path: '/var/log/mail.log',
      },
      {
        id: 'postfix_queue_full',
        name: 'Queue file write error',
        catches: 'Postfix cannot write to the queue — disk full or permission issue.',
        severity: 'critical',
        regex: 'queue file write error',
        exampleLine: 'Jun  1 10:17:00 mail-01 postfix/cleanup[2915]: warning: A7B8C9: queue file write error',
        path: '/var/log/mail.log',
      },
      {
        id: 'postfix_relay_denied',
        name: 'Relay access denied',
        catches: 'A client tried to relay through your server and was refused — possible abuse.',
        severity: 'warning',
        regex: 'Relay access denied',
        exampleLine: 'Jun  1 10:18:00 mail-01 postfix/smtpd[2916]: NOQUEUE: reject: RCPT from unknown[198.51.100.7]: 554 5.7.1 <user@elsewhere.com>: Relay access denied',
        path: '/var/log/mail.log',
      },
    ],
  },

  // ───────────────────────── fail2ban ──────────────────────────────
  {
    slug: 'fail2ban',
    name: 'fail2ban',
    tagline: 'Intrusion-prevention log — bans/unbans across jails.',
    category: 'Mail & security',
    paths: [
      { label: 'fail2ban log', debian: '/var/log/fail2ban.log', rhel: '/var/log/fail2ban.log' },
    ],
    formatExamples: [
      '2026-06-01 10:15:42,123 fail2ban.actions [2913]: NOTICE [sshd] Ban 198.51.100.7',
      '2026-06-01 10:25:42,000 fail2ban.actions [2913]: NOTICE [sshd] Unban 198.51.100.7',
    ],
    patterns: [
      {
        id: 'fail2ban_ban',
        name: 'IP banned',
        catches: 'fail2ban banned an address — track ban volume as an attack indicator.',
        severity: 'warning',
        regex: '\\[\\S+\\] Ban \\d{1,3}(?:\\.\\d{1,3}){3}',
        exampleLine: '2026-06-01 10:15:42,123 fail2ban.actions [2913]: NOTICE [sshd] Ban 198.51.100.7',
        path: '/var/log/fail2ban.log',
      },
      {
        id: 'fail2ban_found',
        name: 'Match found',
        catches: 'A failure matched a jail filter (precedes a ban once maxretry is hit).',
        severity: 'info',
        regex: '\\] Found \\d{1,3}(?:\\.\\d{1,3}){3}',
        exampleLine: '2026-06-01 10:15:40,000 fail2ban.filter [2913]: INFO [sshd] Found 198.51.100.7 - 2026-06-01 10:15:40',
        path: '/var/log/fail2ban.log',
      },
      {
        id: 'fail2ban_error',
        name: 'fail2ban error',
        catches: 'fail2ban itself logged an error — a broken filter/action or unreadable log.',
        severity: 'high',
        regex: 'fail2ban\\.\\S+\\s+\\[\\d+\\]: ERROR',
        exampleLine: '2026-06-01 10:16:00,000 fail2ban.actions [2913]: ERROR Failed to execute ban jail \'sshd\' action \'iptables-multiport\'',
        path: '/var/log/fail2ban.log',
      },
    ],
  },

  // ─────────────────────────── cron ────────────────────────────────
  {
    slug: 'cron',
    name: 'cron',
    tagline: 'Scheduled-job execution log (cron daemon).',
    category: 'Scheduling',
    paths: [
      { label: 'Cron log (dedicated)', debian: '/var/log/cron.log', rhel: '/var/log/cron' },
      { label: 'Cron via syslog', debian: '/var/log/syslog', rhel: '/var/log/messages' },
    ],
    formatExamples: [
      'Jun  1 10:15:01 web-01 CRON[2913]: (root) CMD (/usr/local/bin/backup.sh)',
      'Jun  1 10:15:01 web-01 CRON[2913]: (CRON) error (grandchild #2914 failed with exit status 1)',
    ],
    patterns: [
      {
        id: 'cron_job_error',
        name: 'Cron job failed',
        catches: 'A cron grandchild exited non-zero — a scheduled job failed.',
        severity: 'high',
        regex: 'failed with exit status [1-9]',
        exampleLine: 'Jun  1 10:15:01 web-01 CRON[2913]: (CRON) error (grandchild #2914 failed with exit status 1)',
        path: '/var/log/syslog',
      },
      {
        id: 'cron_no_mta',
        name: 'No MTA to mail output',
        catches: 'Cron produced output but could not mail it — silent job failures may go unnoticed.',
        severity: 'warning',
        regex: 'No MTA installed, discarding output',
        exampleLine: 'Jun  1 10:15:01 web-01 CRON[2913]: (root) MAIL (mailed 120 bytes of output but got status 0x004b from MTA) No MTA installed, discarding output',
        path: '/var/log/syslog',
      },
      {
        id: 'cron_exec',
        name: 'Job executed',
        catches: 'Every command cron runs — useful as a heartbeat that a job is firing at all.',
        severity: 'info',
        regex: 'CRON\\[\\d+\\]: \\(\\S+\\) CMD ',
        exampleLine: 'Jun  1 10:15:01 web-01 CRON[2913]: (root) CMD (/usr/local/bin/backup.sh)',
        path: '/var/log/syslog',
      },
    ],
  },

  // ─────────────────── Docker (json-file driver) ───────────────────
  {
    slug: 'docker',
    name: 'Docker (json-file logs)',
    tagline: 'Per-container logs written by the default json-file logging driver.',
    category: 'Containers',
    paths: [
      { label: 'Container log (json-file)', debian: '/var/lib/docker/containers/*/*-json.log' },
      { label: 'Docker daemon (journald)', debian: 'journalctl -u docker.service' },
    ],
    formatExamples: [
      '{"log":"ERROR could not connect to database: connection refused\\n","stream":"stderr","time":"2026-06-01T10:15:42.123456789Z"}',
      '{"log":"panic: runtime error: invalid memory address or nil pointer dereference\\n","stream":"stderr","time":"2026-06-01T10:16:10.000000000Z"}',
    ],
    patterns: [
      {
        id: 'docker_stderr_error',
        name: 'Container error/exception',
        catches: 'ERROR/FATAL/Exception lines emitted by any container to its json-file log.',
        severity: 'high',
        regex: '"log":"(?:.*\\b(?:ERROR|FATAL|Exception)\\b)',
        exampleLine: '{"log":"ERROR could not connect to database: connection refused\\n","stream":"stderr","time":"2026-06-01T10:15:42.123456789Z"}',
        path: '/var/lib/docker/containers/*/*-json.log',
      },
      {
        id: 'docker_panic',
        name: 'Go / runtime panic',
        catches: 'A panic written to a container log — a crashing Go (or similar) service.',
        severity: 'critical',
        regex: '"log":"panic:',
        exampleLine: '{"log":"panic: runtime error: invalid memory address or nil pointer dereference\\n","stream":"stderr","time":"2026-06-01T10:16:10.000000000Z"}',
        path: '/var/lib/docker/containers/*/*-json.log',
      },
      {
        id: 'docker_oom',
        name: 'Container OOM killed',
        catches: 'A container was OOM-killed (daemon log) — it exceeded its memory limit.',
        severity: 'critical',
        regex: 'oom-kill',
        exampleLine: 'Jun  1 10:17:00 web-01 dockerd[900]: time="2026-06-01T10:17:00Z" level=info msg="oom-kill triggered for container abc123"',
        path: '/var/log/syslog',
      },
    ],
  },
];

export const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'warning', 'info'];

export const SEVERITY_STYLES: Record<Severity, { label: string; badge: string }> = {
  critical: { label: 'Critical', badge: 'bg-red-100 text-red-800 border-red-200' },
  high: { label: 'High', badge: 'bg-orange-100 text-orange-800 border-orange-200' },
  warning: { label: 'Warning', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  info: { label: 'Info', badge: 'bg-sky-100 text-sky-800 border-sky-200' },
};

/** Default Alert24 config path for a pattern (override → first primary path). */
export function patternPath(app: AppLog, pattern: LogPattern): string {
  if (pattern.path) return pattern.path;
  return app.paths[0]?.debian ?? '/var/log/syslog';
}

/** Build the ready-to-paste Alert24 agent log_searches entry for a pattern. */
export function buildAgentConfig(app: AppLog, pattern: LogPattern): string {
  const entry = {
    name: pattern.id,
    source: 'file',
    path: patternPath(app, pattern),
    pattern: pattern.regex,
    pattern_type: 'regex',
  };
  return JSON.stringify(entry, null, 2);
}
